const WIDGET_PERSON_FIELDS = {
    projectContext: {
        shape: "item",
        collection: "stakeholders",
        textField: "name",
        idField: "userId",
    },
    containmentPlan: {
        shape: "item",
        collection: "actions",
        textField: "owner",
        idField: "ownerUserId",
    },
    countermeasureTable: {
        shape: "item",
        collection: "rows",
        textField: "owner",
        idField: "ownerUserId",
    },
    controlPlan: {
        shape: "item",
        collection: "rows",
        textField: "responsible",
        idField: "responsibleUserId",
    },
    stakeholderMap: {
        shape: "item",
        collection: "stakeholders",
        textField: "name",
        idField: "userId",
    },
    // Also the VSM Implementation Plan, whose artifact is a Generic Table.
    genericTable: {
        shape: "userCell",
        collection: "rows",
        record: "cells",
        textField: "displayName",
        idField: "userId",
    },
};
export const PERSON_FIELDS = Object.freeze({
    widgets: Object.freeze(WIDGET_PERSON_FIELDS),
    vsmShape: {
        path: ["dataBox", "owner"],
        textField: "name",
        idField: "userId",
    },
});
/**
 * Restore or drop person links on a save that does not carry the picker's
 * opt-in, BEFORE the stored-schema check. Each item is matched by its id
 * against the stored item (a Generic Table cell by row id and column id):
 *
 * | candidate link       | person text vs stored | result                       |
 * | -------------------- | --------------------- | ---------------------------- |
 * | none (absent / null) | unchanged             | the stored link, copied back |
 * | none (absent / null) | changed               | no link                      |
 * | same as stored       | unchanged             | kept                         |
 * | same as stored       | changed               | dropped (`null`), text kept  |
 * | a new one            | any                   | kept, listed in newPersonIds |
 *
 * A Generic Table `user` cell always leaves with a `userId` (the stored one or
 * `null`), so a model's text-only cell passes the stored schema. Anything the
 * helper cannot read — a non-object, a missing collection — is passed through
 * untouched for the schema check to refuse. Returns `candidate` itself when
 * nothing changes. Never mutates either input.
 */
export function copyBackPersonLinks(kind, candidate, stored, options = {}) {
    const newPersonIds = new Set();
    const spec = PERSON_FIELDS.widgets[kind];
    if (spec === undefined || !isRecord(candidate)) {
        return { params: candidate, newPersonIds: [] };
    }
    const items = candidate[spec.collection];
    if (!Array.isArray(items))
        return { params: candidate, newPersonIds: [] };
    const storedItems = byId(isRecord(stored) ? stored[spec.collection] : []);
    let changed = false;
    const nextItems = items.map((item) => {
        if (!isRecord(item))
            return item;
        const id = item["id"];
        const before = typeof id === "string" ? storedItems.get(id) : undefined;
        const next = spec.shape === "item"
            ? resolveLink(item, before, spec, false, newPersonIds, options)
            : resolveCells(item, before, spec, newPersonIds, options);
        if (next !== item)
            changed = true;
        return next;
    });
    return {
        /* SAFETY: only person-link leaves of `candidate`'s own items differ. */
        params: changed
            ? { ...candidate, [spec.collection]: nextItems }
            : candidate,
        newPersonIds: [...newPersonIds],
    };
}
function resolveCells(row, before, spec, newPersonIds, options) {
    const cells = row[spec.record];
    if (!isRecord(cells))
        return row;
    const storedCells = before?.[spec.record];
    let changed = false;
    const nextCells = {};
    for (const [columnId, cell] of Object.entries(cells)) {
        let next = cell;
        if (isRecord(cell) && cell["type"] === "user") {
            const prior = isRecord(storedCells) ? storedCells[columnId] : undefined;
            next = resolveLink(cell, isRecord(prior) && prior["type"] === "user" ? prior : undefined, spec, true, newPersonIds, options);
        }
        if (next !== cell)
            changed = true;
        nextCells[columnId] = next;
    }
    return changed ? { ...row, [spec.record]: nextCells } : row;
}
/** The d37 rule for one object holding a person's text and link. */
function resolveLink(item, before, spec, fillLink, newPersonIds, options) {
    const link = item[spec.idField];
    if (options.absentOnly === true && link !== undefined)
        return item;
    const storedLink = before?.[spec.idField] ?? null;
    const textUnchanged = before !== undefined && item[spec.textField] === before[spec.textField];
    if (link === undefined || link === null) {
        if (textUnchanged && before !== undefined && spec.idField in before) {
            return withLink(item, spec.idField, before[spec.idField]);
        }
        return fillLink && link === undefined
            ? withLink(item, spec.idField, null)
            : item;
    }
    if (link === storedLink) {
        return textUnchanged ? item : withLink(item, spec.idField, null);
    }
    // A new link is kept as sent; a malformed one is the schema check's to refuse.
    if (typeof link === "string")
        newPersonIds.add(link);
    return item;
}
function withLink(item, idField, link) {
    return item[idField] === link && idField in item
        ? item
        : { ...item, [idField]: link };
}
/** Stored items by id; the first of any duplicated id wins, as `.find()` would. */
function byId(items) {
    const map = new Map();
    if (!Array.isArray(items))
        return map;
    for (const item of items) {
        if (!isRecord(item))
            continue;
        const id = item["id"];
        if (typeof id === "string" && !map.has(id))
            map.set(id, item);
    }
    return map;
}
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
//# sourceMappingURL=person-fields.js.map